import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import type {
  LiveSession,
  LiveSessionMessage,
  LiveSessionStatus,
  SessionEventType,
} from "@/lib/types";

/**
 * Show a browser notification when the session ends while the tab is hidden.
 */
function showCompletionNotification(
  sessionStatus: LiveSessionStatus,
  sessionId: string
) {
  if (
    document.visibilityState === "visible" ||
    !("Notification" in window) ||
    Notification.permission !== "granted"
  ) {
    return;
  }

  const isError = sessionStatus === "error";
  const title = isError ? "Session Error" : "Session Complete";
  const body = isError
    ? "Your agent session encountered an error."
    : "Your agent session has finished working.";

  try {
    new Notification(title, {
      body,
      icon: "/pwa-192x192.png",
      tag: `session-${sessionId}`,
      data: { url: "/sessions" },
    });
  } catch {
    // Notification constructor may throw in some contexts
  }
}

export function useLiveSession(sessionId: string | null) {
  const { user } = useAuth();
  const [messages, setMessages] = useState<LiveSessionMessage[]>([]);
  const [status, setStatus] = useState<LiveSessionStatus>("starting");
  const [isAgentWorking, setIsAgentWorking] = useState(false);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const statusChannelRef = useRef<ReturnType<typeof supabase.channel> | null>(
    null
  );
  const workingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sessionIdRef = useRef<string | null>(sessionId);
  sessionIdRef.current = sessionId;

  // Auto-reset isAgentWorking after 90 seconds with no agent event
  useEffect(() => {
    if (isAgentWorking) {
      workingTimeoutRef.current = setTimeout(() => {
        setIsAgentWorking(false);
      }, 90_000);
    } else {
      if (workingTimeoutRef.current) {
        clearTimeout(workingTimeoutRef.current);
        workingTimeoutRef.current = null;
      }
    }
    return () => {
      if (workingTimeoutRef.current) clearTimeout(workingTimeoutRef.current);
    };
  }, [isAgentWorking]);

  // Load persisted messages on mount / sessionId change
  useEffect(() => {
    if (!sessionId) {
      setMessages([]);
      setStatus("starting");
      setIsAgentWorking(false);
      return;
    }

    let cancelled = false;

    async function loadMessages() {
      // Fetch session status
      const { data: session } = await supabase
        .from("live_sessions")
        .select("status")
        .eq("id", sessionId!)
        .single();
      if (!cancelled && session) {
        setStatus(session.status as LiveSessionStatus);
      }

      // Fetch persisted messages
      const { data, error } = await supabase
        .from("live_session_messages")
        .select("*")
        .eq("session_id", sessionId!)
        .order("created_at", { ascending: true });

      if (!cancelled && !error && data) {
        setMessages(data as LiveSessionMessage[]);
        // Determine if agent is working based on last message
        const last = data[data.length - 1];
        if (last && last.role === "assistant" && last.event_type === "text") {
          setIsAgentWorking(false);
        }
      }
    }

    loadMessages();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  // Re-fetch messages + reconnect channels when app returns to foreground
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState !== "visible") return;
      const id = sessionIdRef.current;
      if (!id) return;

      // Re-fetch latest session status and any messages we missed
      const { data: session } = await supabase
        .from("live_sessions")
        .select("status")
        .eq("id", id)
        .single();
      if (session) {
        setStatus(session.status as LiveSessionStatus);
        if (session.status === "ended" || session.status === "error") {
          setIsAgentWorking(false);
        }
      }

      const { data, error } = await supabase
        .from("live_session_messages")
        .select("*")
        .eq("session_id", id)
        .order("created_at", { ascending: true });

      if (!error && data) {
        setMessages(data as LiveSessionMessage[]);
        const last = data[data.length - 1];
        if (last && last.role === "assistant" && last.event_type === "text") {
          setIsAgentWorking(false);
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  // Subscribe to Realtime broadcast channel for agent events
  useEffect(() => {
    if (!sessionId) return;

    const channel = supabase
      .channel(`session:${sessionId}`)
      .on("broadcast", { event: "agent_event" }, (payload) => {
        // The broadcast payload is a SessionEvent: { type, content, metadata }
        // NOT a LiveSessionMessage. Map it.
        const raw = payload.payload as Record<string, unknown>;
        const eventType = (raw.type || raw.event_type) as string;
        const content = (raw.content || "") as string;
        const role = eventType === "error" ? "system" : "assistant";

        // Skip 'done' events entirely — they're control signals, not messages
        if (eventType === "done") {
          setIsAgentWorking(false);
          return;
        }

        // Skip empty status messages
        if (eventType === "status" && !content) return;

        const msg: LiveSessionMessage = {
          id: crypto.randomUUID(),
          session_id: sessionId!,
          role: role as LiveSessionMessage["role"],
          content,
          event_type: (eventType || "text") as LiveSessionMessage["event_type"],
          metadata: (raw.metadata || {}) as Record<string, unknown>,
          created_at: new Date().toISOString(),
        };

        // Deduplicate: skip if last message has same content and role
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          if (last && last.content === msg.content && last.role === msg.role && last.event_type === msg.event_type) {
            return prev;
          }
          return [...prev, msg];
        });

        // Working state
        if (eventType === "tool_use") {
          setIsAgentWorking(true);
        } else if (eventType === "error" || eventType === "text") {
          setIsAgentWorking(false);
        }
      })
      .on("broadcast", { event: "status_change" }, (payload) => {
        const newStatus = payload.payload?.status as
          | LiveSessionStatus
          | undefined;
        if (newStatus) {
          setStatus(newStatus);
          if (newStatus === "ended" || newStatus === "error") {
            setIsAgentWorking(false);
            showCompletionNotification(newStatus, sessionId);
          }
        }
      })
      .subscribe();

    channelRef.current = channel;

    return () => {
      supabase.removeChannel(channel);
      channelRef.current = null;
    };
  }, [sessionId]);

  // Subscribe to postgres_changes on live_sessions for status updates
  useEffect(() => {
    if (!sessionId) return;

    const channel = supabase
      .channel(`session-status:${sessionId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "live_sessions",
          filter: `id=eq.${sessionId}`,
        },
        (payload) => {
          const updated = payload.new as LiveSession;
          setStatus(updated.status);
          if (updated.status === "ended" || updated.status === "error") {
            setIsAgentWorking(false);
            showCompletionNotification(updated.status, sessionId);
          }
        }
      )
      .subscribe();

    statusChannelRef.current = channel;

    return () => {
      supabase.removeChannel(channel);
      statusChannelRef.current = null;
    };
  }, [sessionId]);

  // Send a user message with optional image attachments.
  // Pass `contextMessages` when reactivating a dead session so the agent
  // runner can restore conversation history.
  const sendMessage = useCallback(
    async (
      content: string,
      images?: Array<{ base64: string; mediaType: string; name: string }>,
      contextMessages?: Array<{ role: string; content: string }>
    ) => {
      if (!sessionId || !user) return;

      const metadata: Record<string, unknown> = {};
      if (images && images.length > 0) {
        metadata.images = images;
      }

      const optimisticMsg: LiveSessionMessage = {
        id: crypto.randomUUID(),
        session_id: sessionId,
        role: "user",
        content,
        event_type: "text",
        metadata,
        created_at: new Date().toISOString(),
      };

      // Optimistic local append
      setMessages((prev) => [...prev, optimisticMsg]);
      setIsAgentWorking(true);

      // Persist to DB (without base64 images — too large for DB)
      await supabase.from("live_session_messages").insert({
        id: optimisticMsg.id,
        session_id: sessionId,
        role: "user",
        content,
        event_type: "text" as SessionEventType,
        metadata: images ? { imageCount: images.length } : {},
      });

      // Broadcast to the session channel (includes base64 for the agent).
      // context_messages is included on reactivation so the runner can
      // restore prior conversation history.
      channelRef.current?.send({
        type: "broadcast",
        event: "user_message",
        payload: { content, images, context_messages: contextMessages },
      });
    },
    [sessionId, user]
  );

  // End the session
  const endSession = useCallback(async () => {
    if (!sessionId) return;

    // Broadcast end event
    channelRef.current?.send({
      type: "broadcast",
      event: "end_session",
      payload: { session_id: sessionId },
    });

    setStatus("ended");
    setIsAgentWorking(false);
  }, [sessionId]);

  return {
    messages,
    status,
    isAgentWorking,
    sendMessage,
    endSession,
  };
}
